import api from './api';

export interface CuentaCorrienteSaldo {
  nroCliente: number;
  nombre: string;
  telefono?: string | null;
  saldoTotal: number;
  saldoVencido: number;
  saldoVigente: number;
  ultimaActualizacion: string;
}

export interface CuentaCorrienteComprobante {
  compania: string;
  nroCliente: string;
  nombre: string;
  fecha: string;
  comprobante: string;
  numero: string;
  importe: number;
  saldoDeb: number;
  saldoHab: number;
  fechaVenc: string;
  saldo: number;
  nroTipoFormulario: string;
  moneda: number;
  nroFormulario: number;
  /** Empresa de origen; solo viene en la consulta unificada de la familia. */
  tenantCode?: string | null;
  tenantName?: string | null;
  /** false: el comprobante es de otra empresa y su nroFormulario no existe en la empresa actual. */
  esEmpresaActual?: boolean;
}

export interface EmpresaFamilia {
  tenantId: number;
  code: string;
  name: string;
  esActual: boolean;
}

/** Listado deuda por vencimiento (GET informes/deuda-por-vencimiento) */
export interface ClienteDeudaPorVencimiento {
  nroCliente: number;
  nombre: string;
  telefono?: string | null;
  direccion?: string | null;
  codigoRuta?: number | null;
  nombreRuta?: string | null;
  nroVendedor?: number | null;
  nombreVendedor?: string | null;
  deudaHastaVencimiento: number;
}

/** Resumen de saldos por zona a fecha (GET informes/saldo-por-zona) */
export interface SaldoPorZonaMovimiento {
  nroFormulario: number;
  fechaEmision: string;
  comprobante: string;
  descripcion?: string | null;
  saldoComp: number;
  debe: number;
  haber: number;
  saldo: number;
}

export interface SaldoPorZonaCliente {
  nroCliente: number;
  nombre: string;
  telefono?: string | null;
  direccion?: string | null;
  ciudad?: string | null;
  provincia?: string | null;
  nroVendedor?: number | null;
  nombreVendedor?: string | null;
  saldoAnterior: number;
  saldo: number;
  movimientos: SaldoPorZonaMovimiento[];
}

export interface SaldoPorZona {
  codigoRuta?: number | null;
  nombreRuta: string;
  clientes: SaldoPorZonaCliente[];
  total: number;
}

export interface SaldoPorZonaInforme {
  fechaDesde: string;
  fechaHasta: string;
  zonas: SaldoPorZona[];
  totalGeneral: number;
}

export interface GetSaldoPorZonaParams {
  fechaDesde: string;
  fechaHasta: string;
  incluirMovimientos?: boolean;
}

export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface MovimientosHistoricosResponse extends PaginatedResponse<MovimientoHistorico> {
  saldoInicial?: number | null;
}

export type SaldosSortBy = 'nombre' | 'monto' | 'longevidad';
export type SaldosSortDir = 'asc' | 'desc';

interface GetSaldosParams {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  nroVendedor?: number;
  sortBy?: SaldosSortBy;
  sortDir?: SaldosSortDir;
}

interface GetComprobantesParams {
  pageNumber?: number;
  pageSize?: number;
  unificado?: boolean;
}

export interface MovimientoHistorico {
  nroCliente: number | null;
  nombreCli: string | null;
  fechaEmision: string | null;
  fechaVencim: string | null;
  descripcionFor: string | null;
  descripcionTip: string | null;
  letra: string | null;
  nroSuc: number;
  nroComprobante: number | null;
  numero: string | null;
  importeTotal: number | null;
  importeCancelado: number | null;
  impCompDebe: number | null;
  impCompHaber: number | null;
  saldoComp: number | null;
  saldoCompDebe: number | null;
  saldoCompHaber: number | null;
  nroTipoFormulario: string | null;
  nroDoc: string | null;
  /** Nombre del formulario; "ANULADO" cuando el comprobante fue anulado */
  nombre: string | null;
  nroFormulario: number | null;
  tenantCode?: string | null;
  tenantName?: string | null;
  esEmpresaActual?: boolean;
}

interface GetMovimientosHistoricosParams {
  fechaDesde?: string;
  fechaHasta?: string;
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
  unificado?: boolean;
}

export interface GetDeudaPorVencimientoParams {
  fechaHasta: string;
  codigoRuta?: number;
  nroVendedor?: number;
}

const parseResponse = <T>(data: unknown): T => {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data) as T;
    } catch (error) {
      console.error('No se pudo parsear la respuesta de Cuenta Corriente:', error);
      throw new Error('Respuesta inválida del servidor de Cuenta Corriente');
    }
  }

  return data as T;
};

const getSaldos = async ({
  pageNumber = 1,
  pageSize = 20,
  searchTerm,
  nroVendedor,
  sortBy,
  sortDir
}: GetSaldosParams = {}): Promise<PaginatedResponse<CuentaCorrienteSaldo>> => {
  const params: Record<string, string | number> = {
    pageNumber,
    pageSize
  };
  
  if (searchTerm) {
    params.searchTerm = searchTerm;
  }

  if (nroVendedor != null && nroVendedor > 0) {
    params.nroVendedor = nroVendedor;
  }

  if (sortBy) {
    params.sortBy = sortBy;
  }

  if (sortDir) {
    params.sortDir = sortDir;
  }
  
  const response = await api.get('/api/CuentaCorriente/saldos', {
    params,
    headers: {
      Accept: 'text/plain'
    }
  });

  return parseResponse<PaginatedResponse<CuentaCorrienteSaldo>>(response.data);
};

const getFamilia = async (): Promise<EmpresaFamilia[]> => {
  const response = await api.get<EmpresaFamilia[]>('/api/CuentaCorriente/familia');
  return Array.isArray(response.data) ? response.data : [];
};

const getSaldoCliente = async (
  nroCliente: number,
  { unificado = false }: { unificado?: boolean } = {}
): Promise<CuentaCorrienteSaldo> => {
  const response = await api.get<CuentaCorrienteSaldo>(
    `/api/CuentaCorriente/cliente/${nroCliente}/saldo`,
    { params: unificado ? { unificado: true } : undefined }
  );
  return response.data;
};

const getComprobantesCliente = async (
  nroCliente: number,
  {
    pageNumber = 1,
    pageSize = 50,
    unificado = false
  }: GetComprobantesParams = {}
): Promise<PaginatedResponse<CuentaCorrienteComprobante>> => {
  const response = await api.get(`/api/CuentaCorriente/cliente/${nroCliente}`, {
    params: {
      pageNumber,
      pageSize,
      ...(unificado ? { unificado: true } : {})
    },
    headers: {
      Accept: 'text/plain'
    }
  });

  return parseResponse<PaginatedResponse<CuentaCorrienteComprobante>>(response.data);
};

const getMovimientosHistoricosCliente = async (
  nroCliente: number,
  {
    fechaDesde,
    fechaHasta,
    searchTerm,
    pageNumber = 1,
    pageSize = 10,
    unificado = false
  }: GetMovimientosHistoricosParams = {}
): Promise<MovimientosHistoricosResponse> => {
  const params: Record<string, string | number | boolean | undefined> = {
    pageNumber,
    pageSize
  };
  if (fechaDesde) params.fechaDesde = fechaDesde;
  if (fechaHasta) params.fechaHasta = fechaHasta;
  if (searchTerm) params.searchTerm = searchTerm;
  if (unificado) params.unificado = true;

  const response = await api.get(
    `/api/CuentaCorriente/cliente/${nroCliente}/movimientos-historicos`,
    {
      params,
      headers: { Accept: 'text/plain' }
    }
  );

  return parseResponse<MovimientosHistoricosResponse>(response.data);
};

export interface EstadisticasCuentaCorriente {
  totalClientesConCuentaCorriente: number;
  saldoTotalGeneral: number;
  saldoVencidoTotal: number;
  totalMovimientos: number;
  ultimaActualizacion: string;
  nroVendedorFiltrado: number | null;
  usuarioFiltrado: string | null;
}

const getEstadisticas = async (params?: {
  nroVendedor?: number | null;
}): Promise<EstadisticasCuentaCorriente> => {
  const query: Record<string, number> = {};
  if (params?.nroVendedor != null && params.nroVendedor > 0) {
    query.nroVendedor = params.nroVendedor;
  }
  const response = await api.get<EstadisticasCuentaCorriente>('/api/CuentaCorriente/estadisticas', {
    params: query,
  });
  return response.data;
};

const getDeudaPorVencimiento = async (
  params: GetDeudaPorVencimientoParams
): Promise<ClienteDeudaPorVencimiento[]> => {
  const query: Record<string, string | number> = {
    fechaHasta: params.fechaHasta
  };
  if (params.codigoRuta != null && params.codigoRuta !== undefined) {
    query.codigoRuta = params.codigoRuta;
  }
  if (params.nroVendedor != null && params.nroVendedor !== undefined) {
    query.nroVendedor = params.nroVendedor;
  }

  const response = await api.get('/api/CuentaCorriente/informes/deuda-por-vencimiento', {
    params: query,
    headers: { Accept: 'text/plain' }
  });

  return parseResponse<ClienteDeudaPorVencimiento[]>(response.data);
};

const getSaldoPorZona = async (params: GetSaldoPorZonaParams): Promise<SaldoPorZonaInforme> => {
  const query: Record<string, string | boolean> = {
    fechaDesde: params.fechaDesde,
    fechaHasta: params.fechaHasta,
    incluirMovimientos: params.incluirMovimientos ?? true
  };

  const response = await api.get('/api/CuentaCorriente/informes/saldo-por-zona', {
    params: query,
    headers: { Accept: 'text/plain' }
  });

  return parseResponse<SaldoPorZonaInforme>(response.data);
};

export interface ImportacionSaldoCtaCtePayload {
  nroTercero: number;
  /** Valor crudo de nroCliente del Excel (CxC); permite NroClienteExterno no numérico. */
  codigoCliente?: string | null;
  tipo: string;
  nroDocumento: string;
  fechaEmision: string;
  fechaVencimiento: string;
  saldoActual: number;
  moneda: number;
  nroSuc: number;
  codigoArticulo: string;
  descripcion?: string | null;
}

export interface ImportacionSaldoCtaCteBulkError {
  indice: number;
  nroTercero: number;
  nroDocumento: string;
  mensaje: string;
}

export interface ImportacionSaldoCtaCteBulkExito {
  indice: number;
  nroFormulario: number;
  nroTercero: number;
  nroDocumento: string;
  letra: string;
  nroSuc: number;
  nroComprobante: number;
}

export interface ImportacionSaldoCtaCteBulkResult {
  exitosos: number;
  fallidos: number;
  errores: ImportacionSaldoCtaCteBulkError[];
  exitos?: ImportacionSaldoCtaCteBulkExito[];
}

export const getComprobanteInternoPdfUrl = (nroFormulario: number): string =>
  `/api/CuentaCorriente/comprobantes-internos/${nroFormulario}/pdf`;

export interface ComprobanteInternoCreateItemPayload {
  codigoArticulo: string;
  descripcion?: string | null;
  cantidad: number;
  precioUnitario: number;
}

export interface ComprobanteInternoCreatePayload {
  tipoCliente: 'C' | 'P';
  tipo: string;
  nroTercero: number;
  nroSuc: number;
  fechaEmision: string;
  fechaVencimiento: string;
  importe?: number;
  moneda: number;
  codigoArticulo?: string;
  descripcion?: string | null;
  items?: ComprobanteInternoCreateItemPayload[];
  referencia?: string | null;
}

export interface ComprobanteInternoItem {
  nroItem: number;
  codigoArticulo: string;
  descripcion?: string | null;
  cantidad?: number | null;
  precioUnitario?: number | null;
  subtotalFinal?: number | null;
  nroCuenta?: number | null;
}

export interface ComprobanteInterno {
  nroFormulario: number;
  nroTipoFormulario: string;
  tipoCliente?: string | null;
  nroCliente?: number | null;
  nombre?: string | null;
  letra?: string | null;
  nroSuc: number;
  nroComprobante?: number | null;
  fechaEmision?: string | null;
  fechaVencim?: string | null;
  importeTotal?: number | null;
  importeCancelado?: number | null;
  codigoMoneda?: number | null;
  tipoIva?: string | null;
  descripcion?: string | null;
  aclaracion?: string | null;
  asentado?: string | null;
  impreso?: string | null;
  usuario?: string | null;
  items: ComprobanteInternoItem[];
}

export interface ComprobanteInternoSearchParams {
  searchTerm?: string;
  pageNumber: number;
  pageSize: number;
  tipoCliente?: 'C' | 'P';
  tipo?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  /** A = anulados; activos = no anulados; omitir = todos */
  asentado?: string;
  nroSuc?: number;
}

export interface ComprobanteInternoPagedResult {
  items: ComprobanteInterno[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

const listComprobantesInternos = async (
  params: ComprobanteInternoSearchParams
): Promise<ComprobanteInternoPagedResult> => {
  const query: Record<string, string | number> = {
    pageNumber: params.pageNumber,
    pageSize: params.pageSize,
    tipoCliente: params.tipoCliente ?? 'C',
  };
  if (params.searchTerm?.trim()) query.searchTerm = params.searchTerm.trim();
  if (params.tipo?.trim()) query.tipo = params.tipo.trim();
  if (params.fechaDesde) query.fechaDesde = params.fechaDesde;
  if (params.fechaHasta) query.fechaHasta = params.fechaHasta;
  if (params.asentado != null && params.asentado !== '') query.asentado = params.asentado;
  if (params.nroSuc != null && params.nroSuc > 0) query.nroSuc = params.nroSuc;

  const response = await api.get<ComprobanteInternoPagedResult>(
    '/api/CuentaCorriente/comprobantes-internos',
    { params: query }
  );
  return response.data;
};

const createComprobanteInterno = async (
  payload: ComprobanteInternoCreatePayload
): Promise<ComprobanteInterno> => {
  const response = await api.post<ComprobanteInterno>(
    '/api/CuentaCorriente/comprobantes-internos',
    payload
  );
  return response.data;
};

const getComprobanteInterno = async (nroFormulario: number): Promise<ComprobanteInterno> => {
  const response = await api.get<ComprobanteInterno>(
    `/api/CuentaCorriente/comprobantes-internos/${nroFormulario}`
  );
  return response.data;
};

const anularComprobanteInterno = async (
  nroFormulario: number,
  motivo?: string
): Promise<void> => {
  await api.post(`/api/CuentaCorriente/comprobantes-internos/${nroFormulario}/anular`, {
    motivo: motivo ?? null,
  });
};

const getComprobanteInternoPdfBlob = async (nroFormulario: number): Promise<Blob> => {
  const response = await api.get(`/api/CuentaCorriente/comprobantes-internos/${nroFormulario}/pdf`, {
    responseType: 'blob',
  });
  return new Blob([response.data], { type: 'application/pdf' });
};

const createSaldosClientesBulk = async (
  filas: ImportacionSaldoCtaCtePayload[]
): Promise<ImportacionSaldoCtaCteBulkResult> => {
  const response = await api.post<ImportacionSaldoCtaCteBulkResult>(
    '/api/CuentaCorriente/saldos-clientes/bulk',
    filas
  );
  return response.data;
};

const createSaldosProveedoresBulk = async (
  filas: ImportacionSaldoCtaCtePayload[]
): Promise<ImportacionSaldoCtaCteBulkResult> => {
  const response = await api.post<ImportacionSaldoCtaCteBulkResult>(
    '/api/CuentaCorriente/saldos-proveedores/bulk',
    filas
  );
  return response.data;
};

const cuentaCorrienteService = {
  getFamilia,
  getSaldos,
  getSaldoCliente,
  getComprobantesCliente,
  getMovimientosHistoricosCliente,
  getEstadisticas,
  getDeudaPorVencimiento,
  getSaldoPorZona,
  createSaldosClientesBulk,
  createSaldosProveedoresBulk,
  listComprobantesInternos,
  createComprobanteInterno,
  getComprobanteInterno,
  anularComprobanteInterno,
  getComprobanteInternoPdfBlob,
};

export default cuentaCorrienteService;
